/**
 * Time helpers for the Notes section. Kept local to the feature because
 * the formats are specific to the notes UX (relative "Just now / 3h ago"
 * row labels, "Editable for 23h" footer hint, calendar-aware grouping).
 */

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

/** Compact relative timestamp used in the row's end column. */
export function relativeNoteTime(iso: string | null, now: Date = new Date()): string {
  if (iso === null) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const minutes = (now.getTime() - ts) / MS_PER_MINUTE;
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${String(Math.floor(minutes))}m ago`;
  const hours = minutes / 60;
  if (hours < 24) return `${String(Math.floor(hours))}h ago`;
  return new Date(ts).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/** Precise timestamp for the expanded footer. */
export function preciseNoteTime(iso: string | null): string {
  if (iso === null) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export type NoteDateGroup = 'today' | 'yesterday' | 'this_week' | 'earlier';

const GROUP_ORDER: readonly NoteDateGroup[] = [
  'today',
  'yesterday',
  'this_week',
  'earlier',
];

/** Calendar-day-aware bucket label for a created_at timestamp. */
export function noteDateGroup(
  iso: string | null,
  now: Date = new Date(),
): NoteDateGroup {
  if (iso === null) return 'earlier';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return 'earlier';
  const created = new Date(ts);
  const createdDay = new Date(
    created.getFullYear(),
    created.getMonth(),
    created.getDate(),
  );
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round(
    (today.getTime() - createdDay.getTime()) / MS_PER_DAY,
  );
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return 'this_week';
  return 'earlier';
}

/**
 * Bucket a sorted-newest-first list into the design's four groups while
 * preserving order. Groups with no entries are omitted.
 */
export function groupNotesByDate<T extends { createdAt: string | null }>(
  notes: readonly T[],
  now: Date = new Date(),
): { group: NoteDateGroup; items: T[] }[] {
  const buckets: Record<NoteDateGroup, T[]> = {
    today: [],
    yesterday: [],
    this_week: [],
    earlier: [],
  };
  for (const note of notes) {
    buckets[noteDateGroup(note.createdAt, now)].push(note);
  }
  return GROUP_ORDER.filter((g) => buckets[g].length > 0).map((g) => ({
    group: g,
    items: buckets[g],
  }));
}
